import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { usePublicIntegration } from '@frontend/context/PublicIntegrationContext';
import {
  POST_AUTH_SERVICE_TARGET_KEY,
  buildServiceLoginPath,
  getServiceAuthPolicy,
  isLaunchableServiceId,
} from '@frontend/config/authPolicyRegistry';
import { resolveServiceUrl } from '@frontend/config/serviceUrlRegistry';

/**
 * Shared post-logout / explicit re-auth shell (ARCH-012).
 * Credentials are entered on Keycloak realm patriotic-projects (theme patriotic).
 * Query: service, switch=1 (force account picker), optional target URL.
 */
export default function ServiceLoginPage() {
  const [searchParams] = useSearchParams();
  const integration = usePublicIntegration();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const serviceId = (searchParams.get('service') || '').trim();
  const switchUser = searchParams.get('switch') === '1';
  const targetParam = searchParams.get('target') || '';

  const policy = useMemo(
    () => (isLaunchableServiceId(serviceId) ? getServiceAuthPolicy(serviceId) : null),
    [serviceId]
  );

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const resumeTarget = useMemo(() => {
    if (targetParam && targetParam.startsWith('/') && !targetParam.startsWith('//')) {
      return `${origin}${targetParam}`;
    }
    if (targetParam.startsWith('http://') || targetParam.startsWith('https://')) {
      try {
        const u = new URL(targetParam);
        if (u.origin === origin) return targetParam;
      } catch {
        /* ignore */
      }
    }
    return policy?.resolveEntryUrl(origin, integration) || null;
  }, [targetParam, origin, policy, integration]);

  const title = policy?.title || 'Patriotic Projects';

  const startLogin = async () => {
    setLoading(true);
    setError('');
    try {
      // NetBird issues JWTs via embedded Dex at /oauth2 (Keycloak is only a Dex IdP).
      // Portal Keycloak OAuth cannot mint Mesh tokens — send the browser to Mesh so the
      // dashboard runs its own Dex→Keycloak login (still the Patriotic Projects IdP UI).
      if (serviceId === 'netbird') {
        window.location.href =
          policy?.resolveEntryUrl(origin, integration) ||
          resolveServiceUrl('netbird', 'sso_entry', origin);
        return;
      }

      if (resumeTarget) {
        sessionStorage.setItem(POST_AUTH_SERVICE_TARGET_KEY, resumeTarget);
      }
      if (switchUser) {
        localStorage.setItem('explicit_logout', 'true');
        sessionStorage.setItem('explicit_logout', 'true');
      }

      const requireLogin =
        switchUser ||
        localStorage.getItem('explicit_logout') === 'true' ||
        sessionStorage.getItem('explicit_logout') === 'true';

      const response = await axios.get('/api/auth/oauth/init', {
        params: { force_login: requireLogin },
      });
      const { authUrl, state } = response.data;
      sessionStorage.setItem('oauth_state', state);
      window.location.href = authUrl;
    } catch (err: unknown) {
      setError(
        axios.isAxiosError<{ message?: string }>(err)
          ? err.response?.data?.message || 'Failed to start sign-in. Please try again.'
          : 'Failed to start sign-in. Please try again.'
      );
      setLoading(false);
    }
  };

  if (!policy) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div className="card" style={{ maxWidth: '480px', width: '100%', textAlign: 'center' }}>
          <h1>Sign in</h1>
          <p style={{ color: 'var(--gray-600)' }}>
            Missing or unknown <code>service</code> parameter.
          </p>
          <Link to="/login" className="btn btn-primary">
            Portal sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div className="card" style={{ maxWidth: '480px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <p
            style={{
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              color: 'var(--gray-500)',
              marginBottom: '0.5rem',
            }}
          >
            Patriotic Projects
          </p>
          <h1 style={{ marginBottom: '0.5rem' }}>Continue to {title}</h1>
          <p style={{ color: 'var(--gray-600)' }}>
            {switchUser
              ? 'Choose an account to sign in. You can pick a different user than before.'
              : 'Sign in with your Patriotic Projects identity (single sign-on).'}
          </p>
        </div>

        {error ? (
          <div className="error" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        ) : null}

        <button
          type="button"
          className="btn btn-primary"
          style={{ width: '100%', fontSize: '1.05rem', padding: '0.9rem' }}
          onClick={startLogin}
          disabled={loading}
        >
          {loading ? 'Redirecting…' : `Sign in to ${title}`}
        </button>

        <p style={{ marginTop: '1.25rem', textAlign: 'center', fontSize: '0.875rem', color: 'var(--gray-500)' }}>
          You will enter credentials on the Patriotic Projects identity page
          {switchUser ? ' (account switch required)' : ''}.
        </p>

        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <Link to="/dashboard" style={{ color: 'var(--primary)', fontWeight: 600 }}>
            Back to dashboard
          </Link>
          {!switchUser ? (
            <>
              {' · '}
              <Link
                to={buildServiceLoginPath(serviceId, { switchUser: true, target: targetParam || undefined })}
                style={{ color: 'var(--primary)' }}
              >
                Use a different account
              </Link>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
